import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';

function WaitingList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { serviceId: prefillServiceId, professionalId, date } = location.state || {};

  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [selectedService, setSelectedService] = useState(prefillServiceId || '');
  const [message, setMessage] = useState('');

  const API_URL = 'https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/';

  useEffect(() => {
    console.log('👤 Utilisateur courant:', user);
    if (user?.user_id) {
      loadRequests();
    }
    loadServices();
    loadProfessionals();
  }, [user]);

  const loadRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/waiting-list/${user.user_id}`);
  
      const sorted = res.data.sort((a, b) => {
        const statusOrder = { pending: 0, notified: 1, accepted: 2, cancelled: 3 };
        const aStatus = statusOrder[a.status] ?? 99;
        const bStatus = statusOrder[b.status] ?? 99;
  
        if (aStatus !== bStatus) {
          return aStatus - bStatus; // priorité au statut
        }
  
        return new Date(b.created_at) - new Date(a.created_at); // puis tri par date
      });
  
      setRequests(sorted);
    } catch (err) {
      console.error('❌ Erreur chargement des demandes', err);
    }
  };

  const loadServices = async () => {
    try {
      const res = await axios.get(`${API_URL}/services`);
      setServices(res.data);
    } catch (err) {
      console.error('❌ Erreur chargement des services', err);
    }
  };

  const loadProfessionals = async () => {
    try {
      const res = await axios.get(`${API_URL}/prestataires`);
      setProfessionals(res.data);
    } catch (err) {
      console.error('❌ Erreur chargement des professionnels', err);
    }
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.service_id === serviceId);
    return service ? service.name : serviceId;
  };

  const getProfessionalName = (professionalId) => {
    const pro = professionals.find(p => p.user_id === professionalId);
    return pro ? `${pro.first_name} ${pro.last_name}` : '—';
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return null;
    const combined = new Date(`${date}T${time}`);
    return combined.toLocaleString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddRequest = async (e) => {
    e.preventDefault();

    if (!user?.user_id) {
      alert("❌ Utilisateur non connecté.");
      return;
    }

    if (!selectedService) {
      alert("❌ Veuillez sélectionner un service.");
      return;
    }

    const { date: selectedDate, startTime, endTime, mode } = location.state || {};

    if (!selectedDate || !startTime || !endTime || !mode) {
      alert("❌ Créneau incomplet. Impossible d’ajouter à la liste d’attente.");
      return;
    }

    try {
      const body = {
        patient_id: user.user_id,
        service_id: selectedService,
        professional_id: professionalId || null,
        date: selectedDate,
        start_time: startTime,
        end_time: endTime,
        mode
      };

      await axios.post(`${API_URL}/waiting-list`, body);

      setMessage('✅ Ajouté à la liste d’attente');
      setSelectedService('');
      loadRequests();

      setTimeout(() => {
        setMessage('');
        navigate('/patient/waitinglistpage');
      }, 1500);
    } catch (err) {
      console.error('❌ Erreur ajout', err);
      setMessage('❌ Erreur ajout');
    }
  };

  const handleCancel = async (requestId) => {
    try {
      await axios.put(`${API_URL}/waiting-list/${requestId}/cancel`);
      setMessage('❌ Demande annulée');
      loadRequests();
    } catch (err) {
      console.error('❌ Erreur annulation', err);
      setMessage('❌ Erreur lors de l’annulation');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <button onClick={() => navigate(-1)}>← Retour</button>

      <h2>Ma liste d’attente</h2>

      {date && (
        <p>
          <strong>📅 Créneau visé :</strong> {date}
          {location.state?.startTime && ` à ${location.state.startTime}`}
        </p>
      )}

      {message && (
        <p style={{ color: message.startsWith('❌') ? 'red' : 'green' }}>{message}</p>
      )}

      <form onSubmit={handleAddRequest} style={{ marginBottom: '20px' }}>
        <label>
          Service :
          <select
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            required
          >
            <option value="">-- Choisir un service --</option>
            {services.map((s) => (
              <option key={s.service_id} value={s.service_id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          style={{ marginLeft: '10px' }}
          disabled={!user?.user_id || !selectedService}
        >
          Ajouter à la liste d’attente
        </button>
      </form>

      <hr />

      <h3>Mes demandes</h3>
      {requests.length > 0 ? (
        <ul>
          {requests.map((req) => (
            <li key={req.request_id} style={{ marginBottom: '15px' }}>
              🔗 <strong>{getServiceName(req.service_id)}</strong>
              {req.professional_id && (
                <> avec <strong>{getProfessionalName(req.professional_id)}</strong></>
              )}
              <br />
              📅 Demandé le : {new Date(req.created_at).toLocaleString('fr-FR')} <br />
              {req.date && req.start_time && (
                <div>
                  📌 Souhaité le : <strong>{formatDateTime(req.date, req.start_time)}</strong>{' '}
                  {req.mode === 'virtual' ? '(en ligne)' : req.mode === 'in_person' ? '(à domicile)' : ''}
                </div>
              )}
              Statut : <strong>{req.status}</strong>
              {req.status === 'pending' && (
                <button
                  onClick={() => handleCancel(req.request_id)}
                  style={{ marginLeft: '10px' }}
                >
                  Annuler
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p>Vous n’avez aucune demande en attente.</p>
      )}
    </div>
  );
}

export default WaitingList;